/* =========================================================================
   ADVELLO — Halloween seasonal layer
   -------------------------------------------------------------------------
   Three small jobs, no dependencies, no external requests:

     1. Live countdowns. Every [data-hw-countdown] on the page is driven by
        ONE shared 1s tick, so ten countdowns cost the same as one. Each
        element carries data-deadline (ISO 8601, shop-local time baked in by
        Liquid). When it passes, the clock swaps to an "it's here" message
        instead of counting up into negative numbers.

     2. The sticky offer bar. Appears once the hero is out of view, can be
        dismissed, and remembers that per campaign — a new deadline means a
        new key, so changing the sale date un-dismisses it for everyone.

     3. Scroll reveal for .hw-reveal, skipped entirely under
        prefers-reduced-motion.

   Everything degrades gracefully: if this file fails to load, the countdown
   still renders server-side values from Liquid and every link still works.
   ========================================================================= */

(function () {
  "use strict";

  var reduceMotion = false;
  try {
    reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (e) {}

  /* ----------------------------------------------------------- countdowns */

  var UNITS = ["days", "hours", "minutes", "seconds"];
  var clocks = [];
  var timer = null;

  function pad(value, length) {
    var out = String(Math.max(0, value));
    while (out.length < length) out = "0" + out;
    return out;
  }

  function collect(root) {
    var nodes = (root || document).querySelectorAll("[data-hw-countdown]");

    Array.prototype.forEach.call(nodes, function (node) {
      if (node.hwBound) return;
      node.hwBound = true;

      var deadline = Date.parse(node.getAttribute("data-deadline") || "");
      if (isNaN(deadline)) return;

      var clock = {
        node: node,
        deadline: deadline,
        over: false,
        last: {},
        cells: {},
        live: node.querySelector("[data-hw-live]"),
        label: node.querySelector("[data-hw-countdown-label]")
      };

      UNITS.forEach(function (unit) {
        clock.cells[unit] = node.querySelector('[data-hw-unit="' + unit + '"]');
      });

      if (clock.label) clock.label.setAttribute("data-default-label", clock.label.textContent || "");
      clocks.push(clock);
      render(clock, true);
    });

    if (clocks.length && !timer) {
      timer = window.setInterval(tick, 1000);
      // Re-sync the moment the tab comes back — background tabs throttle timers.
      document.addEventListener("visibilitychange", function () {
        if (!document.hidden) tick();
      });
    }
  }

  function render(clock, first) {
    var remaining = clock.deadline - Date.now();

    if (remaining <= 0) {
      if (!clock.over) {
        clock.over = true;
        if (clock.label) clock.label.textContent = "";
        clock.node.classList.add("is-over");
        clock.node.dispatchEvent(
          new CustomEvent("hw:countdown-ended", { bubbles: true })
        );
      }
      return;
    }

    var total = Math.floor(remaining / 1000);
    var value = {
      days: Math.floor(total / 86400),
      hours: Math.floor((total % 86400) / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60
    };

    if (clock.label) clock.label.textContent = total < 86400 ? "Final day" : clock.label.getAttribute("data-default-label");

    UNITS.forEach(function (unit) {
      var cell = clock.cells[unit];
      if (!cell) return;

      var next = unit === "days" ? String(value[unit]) : pad(value[unit], 2);
      if (clock.last[unit] === next) return;

      clock.last[unit] = next;
      cell.textContent = next;

      if (first || reduceMotion) return;

      var box = cell.closest(".hw-unit") || cell;
      box.classList.remove("is-ticking");
      // Force a reflow so the animation restarts on every change.
      void box.offsetWidth;
      box.classList.add("is-ticking");
    });

    // Screen readers get minute-level granularity, not a per-second barrage.
    if (clock.live && (first || value.seconds === 0)) {
      clock.live.textContent =
        value.days + " days, " + value.hours + " hours and " +
        value.minutes + " minutes left";
    }
  }

  function tick() {
    for (var i = 0; i < clocks.length; i++) render(clocks[i], false);
  }

  /* ------------------------------------------------------------ offer bar */

  function initBar() {
    var bar = document.querySelector("[data-hw-bar]");
    if (!bar) return;

    // Product pages already own the bottom of the screen with the sticky
    // add-to-cart bar. Two stacked bars help nobody, and the one that sells
    // the product wins, so stand down here.
    if (document.querySelector("[data-adv-sticky]")) return;

    var key = "advello-hw-bar-" + (bar.getAttribute("data-campaign") || "default");
    var dismissed = false;

    try {
      dismissed = window.localStorage.getItem(key) === "1";
    } catch (e) {}

    if (dismissed) return;

    bar.classList.add("is-enabled");
    // Reserve the space up front rather than when the bar slides in, so the
    // page never jumps under the reader mid-scroll.
    document.documentElement.classList.add("hw-bar-enabled");

    var after = parseInt(bar.getAttribute("data-after") || "600", 10);
    var shown = false;

    function onScroll() {
      var past = (window.pageYOffset || document.documentElement.scrollTop) > after;
      if (past === shown) return;
      shown = past;
      bar.classList.toggle("is-visible", past);
      // Lifts the chat launcher and the spin wheel clear of the bar, the same
      // way the product page lifts them clear of the sticky add-to-cart.
      document.body.classList.toggle("hw-bar-on", past);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    var close = bar.querySelector("[data-hw-bar-close]");
    if (close) {
      close.addEventListener("click", function () {
        bar.classList.remove("is-visible");
        document.body.classList.remove("hw-bar-on");
        document.documentElement.classList.remove("hw-bar-enabled");
        window.setTimeout(function () { bar.classList.remove("is-enabled"); }, 450);
        try { window.localStorage.setItem(key, "1"); } catch (e) {}
      });
    }

    // A finished campaign should not keep nagging.
    bar.addEventListener("hw:countdown-ended", function () {
      bar.classList.remove("is-visible");
    });
  }

  /* --------------------------------------------------------- scroll reveal */

  function initReveal(root) {
    var nodes = (root || document).querySelectorAll(".hw-reveal");
    if (!nodes.length) return;

    if (reduceMotion || !("IntersectionObserver" in window)) {
      Array.prototype.forEach.call(nodes, function (n) { n.classList.add("is-in"); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-in");
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
    );

    Array.prototype.forEach.call(nodes, function (n) {
      if (n.hwRevealBound) return;
      n.hwRevealBound = true;
      observer.observe(n);
    });
  }

  /* ------------------------------------------------------------- lifecycle */

  function boot() {
    collect(document);
    initBar();
    initReveal(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Theme editor: re-bind when a section is re-rendered.
  document.addEventListener("shopify:section:load", function (event) {
    collect(event.target);
    initReveal(event.target);
    initBar();
  });
})();
